// src/hooks/useCaptureManager.ts
/**
 * Capture 管理 Hook
 *
 * 處理快速捕捉、模態框控制和最近記錄
 * 從 Today.tsx 抽取，保持功能完整性
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { Capture, CaptureFilters } from '../types/capture';
import { getCaptures } from '../services/api/captures';
import {
  getUnprocessedCaptures,
  saveCaptures
} from '../services/indexedDB';
import { logger } from '../utils/logger';

export interface UseCaptureManagerOptions {
  userId: string;
  isMobile: boolean;
  onCreateCapture: (content: string) => Promise<Capture>;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export interface UseCaptureManagerReturn {
  // 狀態
  recentCaptures: Capture[];
  captureInput: string;
  showCaptureModal: boolean;
  modalInitialContent: string;
  // 方法
  setCaptureInput: (value: string) => void;
  handleQuickCapture: () => Promise<void>;
  handleExpandCapture: () => void;
  handleCaptureModalSubmit: (content: string, relatedToCurrentTimeBox: boolean) => Promise<void>;
  closeCaptureModal: () => void;
  fetchRecentCaptures: () => Promise<void>;
}

export function useCaptureManager(options: UseCaptureManagerOptions): UseCaptureManagerReturn {
  const { userId, isMobile, onCreateCapture, onSuccess, onError } = options;

  // Quick Capture 相關狀態
  const [recentCaptures, setRecentCaptures] = useState<Capture[]>([]);
  const [captureInput, setCaptureInput] = useState('');

  // Capture Modal 狀態
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [modalInitialContent, setModalInitialContent] = useState('');
  const hasAutoExpandedCapture = useRef(false);

  /**
   * 重置自動展開標記（當輸入框清空時）
   */
  useEffect(() => {
    if (captureInput === '') {
      hasAutoExpandedCapture.current = false;
    }
  }, [captureInput]);

  /**
   * 字數檢測 - 超過閾值自動展開 Modal
   */
  useEffect(() => {
    if (!captureInput || showCaptureModal || hasAutoExpandedCapture.current) return;

    const threshold = isMobile ? 60 : 100;

    if (captureInput.length >= threshold) {
      hasAutoExpandedCapture.current = true;
      setModalInitialContent(captureInput);
      setShowCaptureModal(true);
    }
  }, [captureInput, isMobile, showCaptureModal]);

  /**
   * 獲取最近的 Captures - 優化版本
   */
  const fetchRecentCaptures = useCallback(async () => {
    try {
      // 1. 先從 IndexedDB 快速載入
      const localCaptures = await getUnprocessedCaptures(userId, 4);
      if (localCaptures.length > 0) {
        setRecentCaptures(localCaptures);
      }

      // 2. 背景同步 Firestore
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const filters: Partial<CaptureFilters> = {
        userId,
        processed: false,
        createdAfter: sevenDaysAgo
      };

      const remoteCaptures = await getCaptures(filters);
      const latestCaptures = remoteCaptures.slice(0, 4);

      // 3. 更新狀態並儲存到 IndexedDB
      setRecentCaptures(latestCaptures);
      if (latestCaptures.length > 0) {
        await saveCaptures(userId, latestCaptures);
      }
    } catch (error) {
      logger.error('CaptureManager', 'Failed to fetch recent captures', { error });
    }
  }, [userId]);

  /**
   * 處理 Quick Capture 提交
   */
  const handleQuickCapture = useCallback(async () => {
    if (!captureInput.trim()) return;

    try {
      await onCreateCapture(captureInput.trim());

      setCaptureInput('');
      onSuccess('Capture added successfully');
      await fetchRecentCaptures();
    } catch (error) {
      logger.error('CaptureManager', 'Failed to create capture', { error });
      onError('Failed to add capture');
    }
  }, [captureInput, onCreateCapture, onSuccess, onError, fetchRecentCaptures]);

  /**
   * 手動展開 Capture Modal
   */
  const handleExpandCapture = useCallback(() => {
    setModalInitialContent(captureInput);
    setShowCaptureModal(true);
  }, [captureInput]);

  /**
   * 處理 Capture Modal 提交
   */
  const handleCaptureModalSubmit = useCallback(async (content: string, _relatedToCurrentTimeBox: boolean) => {
    if (!content.trim()) return;

    try {
      await onCreateCapture(content.trim());

      setCaptureInput('');
      setShowCaptureModal(false);
      setModalInitialContent('');
      onSuccess('Capture added successfully');
      await fetchRecentCaptures();
    } catch (error) {
      logger.error('CaptureManager', 'Failed to create capture', { error });
      onError('Failed to add capture');
    }
  }, [onCreateCapture, onSuccess, onError, fetchRecentCaptures]);

  /**
   * 關閉 Capture Modal
   */
  const closeCaptureModal = useCallback(() => {
    setShowCaptureModal(false);
    setModalInitialContent('');
  }, []);

  return {
    recentCaptures,
    captureInput,
    showCaptureModal,
    modalInitialContent,
    setCaptureInput,
    handleQuickCapture,
    handleExpandCapture,
    handleCaptureModalSubmit,
    closeCaptureModal,
    fetchRecentCaptures,
  };
}
